import React, { useState, useEffect, useCallback } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useAdminHeaderActions } from '../../contexts/AdminHeaderActionsContext';
import { getAccessToken } from '../../utils/authToken';
import { ExerciseTypeManagement } from './ExerciseTypeManagement';
import { exerciseTypeService } from '../../services/exerciseTypeService';
import { ExerciseType } from '../../types/exerciseType';

interface ExerciseTemplate {
  id: number;
  name: string;
  exercise_type: string;
  description: string;
  scenario_template: string;
  objectives: string[];
  participants_required: string[];
  duration_minutes: number;
  difficulty_level: string;
  industry_specific: boolean;
  industry_types: string[];
  is_active: boolean;
  created_at: string;
}

const TemplateManagement: React.FC = () => {
  const { setActions } = useAdminHeaderActions();
  const [templates, setTemplates] = useState<ExerciseTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ExerciseTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<ExerciseTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    exercise_type: '',
    description: '',
    scenario_template: '',
    objectives: '',
    participants_required: '',
    duration_minutes: 120,
    difficulty_level: 'intermediate',
    industry_specific: false,
    industry_types: ''
  });

  const [activeTab, setActiveTab] = useState<'templates' | 'exercise_types'>('templates');
  const [exerciseTypes, setExerciseTypes] = useState<ExerciseType[]>([]);

  const resetForm = () => ({
    name: '',
    exercise_type: '',
    description: '',
    scenario_template: '',
    objectives: '',
    participants_required: '',
    duration_minutes: 120,
    difficulty_level: 'intermediate',
    industry_specific: false,
    industry_types: ''
  });

  const openCreateForm = useCallback(() => {
    setShowCreateForm(true);
    setEditingTemplate(null);
    setFormData(resetForm());
  }, []);

  const fetchExerciseTypes = useCallback(async () => {
    try {
      const types = await exerciseTypeService.getExerciseTypes(true);
      setExerciseTypes(types);
    } catch (err) {
      console.error('Failed to load exercise types for templates:', err);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchExerciseTypes();
  }, [fetchExerciseTypes]);

  useEffect(() => {
    setActions(null);
    return () => setActions(null);
  }, [setActions]);

  const fetchTemplates = async () => {
    try {
      const token = getAccessToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const response = await fetch(`${API_BASE_URL}/v1/tabletop-exercises/templates/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        setError('Failed to load templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const token = getAccessToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const payload = {
        ...formData,
        objectives: formData.objectives.split('\n').filter(obj => obj.trim()),
        participants_required: formData.participants_required.split('\n').filter(role => role.trim()),
        industry_types: formData.industry_types.split(',').map(type => type.trim()).filter(type => type)
      };

      const url = editingTemplate
        ? `${API_BASE_URL}/v1/tabletop-exercises/templates/${editingTemplate.id}`
        : `${API_BASE_URL}/v1/tabletop-exercises/templates/`;

      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await fetchTemplates();
        setShowCreateForm(false);
        setEditingTemplate(null);
        setFormData(resetForm());
      } else {
        const errorData = await response.json();
        setError(errorData.detail || `Failed to ${editingTemplate ? 'update' : 'create'} template`);
      }
    } catch (err) {
      setError('Error saving template');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (template: ExerciseTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      exercise_type: template.exercise_type,
      description: template.description,
      scenario_template: template.scenario_template,
      objectives: template.objectives.join('\n'),
      participants_required: template.participants_required.join('\n'),
      duration_minutes: template.duration_minutes,
      difficulty_level: template.difficulty_level,
      industry_specific: template.industry_specific,
      industry_types: template.industry_types.join(', ')
    });
    setShowCreateForm(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    setIsDeleting(true);
    setError(null);

    try {
      const token = getAccessToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const response = await fetch(
        `${API_BASE_URL}/v1/tabletop-exercises/templates/${templateToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        setTemplateToDelete(null);
        await fetchTemplates();
      } else {
        setError('Failed to delete template');
      }
    } catch (err) {
      setError('Error deleting template');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleActive = async (template: ExerciseTemplate) => {
    try {
      const token = getAccessToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const response = await fetch(`${API_BASE_URL}/v1/tabletop-exercises/templates/${template.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...template,
          is_active: !template.is_active
        }),
      });

      if (response.ok) {
        await fetchTemplates();
      } else {
        setError('Failed to update template status');
      }
    } catch (err) {
      setError('Error updating template status');
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Top Tabs: Templates vs Exercise Types */}
      <div className="flex items-center space-x-1 border-b border-slate-200 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'templates'
              ? 'border-indigo-900 text-indigo-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Scenario Templates ({templates.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('exercise_types')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'exercise_types'
              ? 'border-indigo-900 text-indigo-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Exercise Types Catalog ({exerciseTypes.length})
        </button>
      </div>

      {activeTab === 'exercise_types' ? (
        <ExerciseTypeManagement />
      ) : (
        <>
          <div className="mb-6 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Exercise Templates</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Configure tabletop exercise scenarios, injects, and guidelines
              </p>
            </div>
            <button
              type="button"
              onClick={openCreateForm}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-colors shrink-0"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Add Template</span>
            </button>
          </div>

      {/* Templates List or Empty State */}
      {templates.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
          <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
            </svg>
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-1">No Exercise Templates Found</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto mb-5">
            Create structured tabletop simulation templates (cyber attacks, power outages, data breaches) for client exercises.
          </p>
          <button
            type="button"
            onClick={openCreateForm}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-colors"
          >
            + Create First Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(template)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateToDelete(template)}
                  className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                  aria-label={`Delete ${template.name}`}
                  title="Delete template"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">{template.description}</p>

            <div className="space-y-2 text-sm text-gray-500">
              <div className="flex justify-between">
                <span>Type:</span>
                <span className="capitalize">{template.exercise_type}</span>
              </div>
              <div className="flex justify-between">
                <span>Duration:</span>
                <span>{template.duration_minutes} min</span>
              </div>
              <div className="flex justify-between">
                <span>Level:</span>
                <span className="capitalize">{template.difficulty_level}</span>
              </div>
              <div className="flex justify-between">
                <span>Status:</span>
                <button
                  onClick={() => toggleActive(template)}
                  className={`px-2 py-1 rounded text-xs ${
                    template.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {template.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete template?</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete{' '}
              <span className="font-medium text-gray-900">{templateToDelete.name}</span>? This
              action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setTemplateToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingTemplate ? 'Edit Template' : 'Create Template'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingTemplate(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Template Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Exercise Type
                    </label>
                    <select
                      name="exercise_type"
                      value={formData.exercise_type}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select exercise type...</option>
                      {exerciseTypes
                        .filter((t) => t.is_active || t.key === formData.exercise_type)
                        .map((t) => (
                          <option key={t.key} value={t.key}>
                            {t.icon ? `${t.icon} ` : ''}{t.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      name="duration_minutes"
                      value={formData.duration_minutes}
                      onChange={handleInputChange}
                      required
                      min="30"
                      max="480"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Difficulty Level
                    </label>
                    <select
                      name="difficulty_level"
                      value={formData.difficulty_level}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scenario Template
                  </label>
                  <textarea
                    name="scenario_template"
                    value={formData.scenario_template}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe the scenario that will be used for this exercise..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Objectives (one per line)
                  </label>
                  <textarea
                    name="objectives"
                    value={formData.objectives}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Exercise objective 1&#10;Exercise objective 2&#10;Exercise objective 3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Required Participants (one per line)
                  </label>
                  <textarea
                    name="participants_required"
                    value={formData.participants_required}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="CEO&#10;IT Manager&#10;Operations Manager"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="industry_specific"
                    checked={formData.industry_specific}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    Industry Specific Template
                  </label>
                </div>

                {formData.industry_specific && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Applicable Industries (comma-separated)
                    </label>
                    <input
                      type="text"
                      name="industry_types"
                      value={formData.industry_types}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Healthcare, Finance, Manufacturing"
                    />
                  </div>
                )}

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingTemplate(null);
                    }}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isSubmitting
                      ? (editingTemplate ? 'Updating...' : 'Creating...')
                      : (editingTemplate ? 'Update Template' : 'Create Template')
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default TemplateManagement;
